import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import type { FilterSort } from '@hooks/useFilter/Filter.tsx';
import type { FilterConfig, SortableColumn } from '@components/RingilFilters/types';
import { AllFiltersSidebar } from '@components/RingilFilters/v2/AllFiltersSidebar';
import { useHeartedFilters } from '@components/RingilFilters/v2/useHeartedFilters';
import { defaultHeartedKeys } from '@components/RingilFilters/v2/filterChipUtils';

interface ComponentProps {
  visible: boolean;
  onClose: () => void;
  /** Name of the open tab, shown under the heading. */
  viewName: string;
  filterName: string;
  filtersConf: FilterConfig[];
  filter: unknown;
  applyFilterValue: (key: string, value: unknown) => void;
  loading: boolean;
  onReset: () => void;
  /** "Uložit filtr": the tab's filter, sort and hearts are saved. Absent for a colleague's shared tab. */
  onSave?: () => void;
  sort?: FilterSort;
  applySorting?: (sortField: string, sortDirection: 'ASC' | 'DESC') => void;
  sortableColumns?: SortableColumn[];
}

/**
 * "Výchozí nastavení filtrů" (Figma 21940:63154): the open tab's sort, filters and hearted filters.
 *
 * The v2 "All filters" sidebar, titled for the tab. Hearts are read through the same hook as the chip
 * bar; inside a RingilDataTable v3 with tabs that hook is served by the tab (see RingilDataTableV3), so
 * hearting a filter edits the tab's working copy. Opened from the tab menu and from the "All filters"
 * button alike — one sidebar, not two that could disagree.
 */
const TabFiltersSidebar: React.FC<ComponentProps> = ({
  visible,
  onClose,
  viewName,
  filterName,
  filtersConf,
  filter,
  applyFilterValue,
  loading,
  onReset,
  onSave,
  sort,
  applySorting,
  sortableColumns,
}) => {
  const { tr } = useTranslationLgs();
  const { isHearted, toggleHearted } = useHeartedFilters(filterName, defaultHeartedKeys(filtersConf));

  return (
    <AllFiltersSidebar
      visible={visible}
      onClose={onClose}
      heading={tr('TableViews.defaultFilters', 'Default filters')}
      subheading={viewName}
      filtersConf={filtersConf}
      filter={filter}
      applyFilterValue={applyFilterValue}
      loading={loading}
      isHearted={isHearted}
      toggleHearted={toggleHearted}
      onReset={onReset}
      onSaveFilter={() => onSave?.()}
      savingEnabled={!!onSave}
      sort={sort}
      applySorting={applySorting}
      sortableColumns={sortableColumns}
    />
  );
};

export default TabFiltersSidebar;
